import React from 'react'
import { User, UserRoleKind } from '../../../../api/users'
import { Organization } from '../../../../api/organizations'
import {
  getUserRole,
  getWorkerAbsentLabel,
  getWorkerAndOrganizationAssociation,
  getWorkerAvailabilityForOrganization,
  getActiveContractualSchedule,
  hasContractualScheduleWithTimeSlots,
} from '../../../../utils/users'
import usersLocales from '../../../../../../locales/users'
import { formatCompleteDateTime } from '../../../../utils/date'
import RoleStatus from '../../role-status'
import { isAfter } from 'date-fns'
import ResendUserInvitationButton from '../../buttons/resend-invitation'
import DiscontinueAssociationButton from '../../buttons/discontinue-association'
import EditUserButton from '../../buttons/edit-user'
import ShowQrCodeButton from '../../buttons/show-qr-code'
import { Column } from 'react-table'
import useStore from '../../../../store'
import { InfoIcon, CalendarIcon } from '../../../icons'
import { Dropdown, Element, Form, Icon } from 'react-bulma-components'
import { ArrowDownIcon } from '@heroicons/react/outline'
import DeleteAssociationButton from '../../buttons/delete-association'
import { ResourceLinkCell } from '../../../table/cells'
import { sortByIgnoreCase } from '../../../../utils/arrays'

export type UserListRowKeys =
  | 'select'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'internalRef'
  | 'internalComment'
  | 'internalInformation'
  | 'createdAt'
  | 'organizationRole'
  | 'associationStatus'
  | 'roleStatus'
  | 'availability'
  | 'weeklyHoursWorked'
  | 'qrCode'
  | 'actions'

type UserListRowData = Partial<Record<UserListRowKeys, React.ReactNode>> & {
  userId?: string // ID de l'utilisateur pour la sélection (ajouté dans useMemo)
}

const useUsersTableData = (
  users: User[],
  roleKind: UserRoleKind | 'offlineWorker' | 'employerWorker',
  columns: UserListRowKeys[],
  enableSelection = false,
  selectedUserIds: string[] = [],
  onSelectionChange?: (selectedIds: string[]) => void,
): { users: UserListRowData[]; columns: Column<UserListRowData>[] } => {
  const currentUser = useStore(state => state.session.currentUser as User)
  const currentOrganization = useStore(state => state.session.currentOrganization as Organization)

  const handleSelectAll = React.useCallback(() => {
    if (!onSelectionChange) return
    const allUserIds = sortByIgnoreCase(users, 'lastName').map(user => user._id)
    if (selectedUserIds.length === allUserIds.length) {
      onSelectionChange([])
    } else {
      onSelectionChange(allUserIds)
    }
  }, [users, selectedUserIds, onSelectionChange])

  const handleSelectRow = React.useCallback(
    (userId: string) => {
      if (!onSelectionChange) return
      if (selectedUserIds.includes(userId)) {
        onSelectionChange(selectedUserIds.filter(id => id !== userId))
      } else {
        onSelectionChange([...selectedUserIds, userId])
      }
    },
    [selectedUserIds, onSelectionChange],
  )

  return React.useMemo(() => {
    const usersAsRows = sortByIgnoreCase(users, 'lastName').map(user => ({
      ...mapUserToRowData(user, currentOrganization, roleKind, currentUser),
      userId: user._id,
      select: enableSelection ? (
        <Form.Checkbox
          checked={selectedUserIds.includes(user._id)}
          onChange={() => handleSelectRow(user._id)}
        />
      ) : undefined,
    }))

    const tableColumns = buildColumns(
      columns,
      enableSelection,
      handleSelectAll,
      selectedUserIds,
      users.length,
    )

    return {
      users: usersAsRows,
      columns: tableColumns,
    }
  }, [
    users,
    currentOrganization,
    roleKind,
    currentUser,
    enableSelection,
    selectedUserIds,
    handleSelectRow,
    handleSelectAll,
  ])
}

export default useUsersTableData

const buildColumns = (
  requestedColumns: UserListRowKeys[],
  enableSelection = false,
  onSelectAll?: () => void,
  selectedUserIds: string[] = [],
  totalUsers = 0,
): Column<UserListRowData>[] => {
  const columns: Column<UserListRowData>[] = []

  // Ajouter la colonne de sélection en premier si activée
  if (enableSelection) {
    columns.push({
      Header: (
        <Form.Checkbox
          checked={totalUsers > 0 && selectedUserIds.length === totalUsers}
          onChange={onSelectAll}
        />
      ),
      accessor: 'select',
      id: 'select',
      width: 50,
    })
  }

  // Ajouter les autres colonnes
  requestedColumns.forEach(column => {
    const tableColumn = allColumns.find(c => c.accessor === column)
    if (tableColumn) {
      columns.push(tableColumn)
    }
  })

  return columns
}

const mapUserToRowData = (
  user: User,
  organization: Organization,
  roleKind: UserRoleKind | 'offlineWorker' | 'employerWorker',
  currentUser: User,
): UserListRowData => {
  const association =
    roleKind === 'worker' ? getWorkerAndOrganizationAssociation(user, organization) : undefined
  const realUserRoleKind =
    roleKind === 'offlineWorker' || roleKind === 'employerWorker' ? 'worker' : roleKind
  const userRole = getUserRole(user, realUserRoleKind, organization?._id)
  const availability =
    roleKind === 'worker' && getWorkerAvailabilityForOrganization(user, organization)

  const associationOrRoleStatus = roleKind === 'worker' ? association?.status : userRole?.status

  const canAssociationBeDiscontinuedOrDeleted =
    associationOrRoleStatus && roleKind !== 'superAdmin' && user._id !== currentUser._id
  const canAssociationBeDiscontinued =
    canAssociationBeDiscontinuedOrDeleted &&
    (associationOrRoleStatus === 'accepted' || associationOrRoleStatus === 'pending')
  const canAssociationBeDeleted =
    canAssociationBeDiscontinuedOrDeleted &&
    roleKind === 'worker' &&
    (associationOrRoleStatus === 'rejected' || associationOrRoleStatus === 'discontinued')
  const canShowQrCode = association?.status === 'accepted' || association?.status === 'pending'
  const canUpdateUserInformation =
    roleKind !== 'interimAgencyMember' &&
    (associationOrRoleStatus === 'accepted' || associationOrRoleStatus === 'pending') &&
    currentUser._id !== user._id

  const canRegenerateQrCode = user.appAccess === 'none' || user._id === currentUser._id

  return {
    lastName:
      roleKind !== 'worker' ? (
        user.lastName
      ) : (
        <ResourceLinkCell resource={user} path={`/workers/`} label={user.lastName} />
      ),
    firstName:
      roleKind !== 'worker' ? (
        user.firstName
      ) : (
        <ResourceLinkCell resource={user} path={`/workers/`} label={user.firstName} />
      ),
    email: user.email,
    internalRef: association?.internalRef,
    internalComment: association?.internalComment && (
      <Element
        data-tooltip={association.internalComment}
        data-test={`internal-comment-${user._id}`}
      >
        <Icon size="auto">
          <InfoIcon />
        </Icon>
      </Element>
    ),
    internalInformation: association?.internalInformation && (
      <Element
        data-tooltip={association.internalInformation}
        data-test={`internal-information-${user._id}`}
      >
        <Icon size="auto">
          <InfoIcon />
        </Icon>
      </Element>
    ),
    createdAt: formatCompleteDateTime(user.createdAt),
    organizationRole:
      userRole?.organizationRole && usersLocales.organizationRoles[userRole.organizationRole],
    associationStatus: association && <RoleStatus role={association} showDate />,
    roleStatus: userRole ? <RoleStatus role={userRole} showDate /> : '',
    availability:
      availability &&
      availability.absentFrom &&
      (!availability.absentTo || isAfter(new Date(availability.absentTo), new Date()))
        ? getWorkerAbsentLabel(availability, user, true)
        : '',
    weeklyHoursWorked:
      roleKind === 'worker' && association
        ? (() => {
            const weeklyHours = getActiveContractualSchedule(association)?.weeklyHoursWorked
            const hasSchedule = hasContractualScheduleWithTimeSlots(association)
            return (
              <Element display="flex" alignItems="center" style={{ gap: '0.5rem' }}>
                {weeklyHours !== undefined ? weeklyHours : ''}
                {hasSchedule && (
                  <Icon size={'small'} data-tooltip="Horaire avec créneaux horaires">
                    <CalendarIcon />
                  </Icon>
                )}
              </Element>
            )
          })()
        : undefined,
    qrCode: canShowQrCode ? (
      <ShowQrCodeButton user={user} canRegenerate={canRegenerateQrCode} />
    ) : (
      ''
    ),
    actions: (
      <Dropdown
        right
        icon={<ArrowDownIcon />}
        label="Actions"
        data-test={`user-actions-${user._id}`}
        className="is-small"
      >
        {canUpdateUserInformation ? (
          <Dropdown.Item
            renderAs="div"
            data-test={`edit-user-${user._id}`}
            value={`${user._id}_edit`}
          >
            <EditUserButton
              user={user}
              userRoleKind={realUserRoleKind}
              label="Modifier les informations"
              color={'ghost'}
            />
          </Dropdown.Item>
        ) : (
          <></>
        )}

        {associationOrRoleStatus && associationOrRoleStatus !== 'accepted' ? (
          <Dropdown.Item
            renderAs="div"
            data-test={`resend-invitation-${user._id}`}
            value={`${user._id}_resend`}
          >
            <ResendUserInvitationButton
              userEmail={user.email}
              organization={organization}
              roleKind={realUserRoleKind}
              color={'ghost'}
            />
          </Dropdown.Item>
        ) : (
          <></>
        )}

        {canAssociationBeDiscontinued ? (
          <Dropdown.Item
            renderAs="div"
            data-test={`discontinue-association-${user._id}`}
            value={`${user._id}_discontinue`}
          >
            <DiscontinueAssociationButton
              user={user}
              organization={organization}
              roleKind={realUserRoleKind}
              color={'ghost'}
            />
          </Dropdown.Item>
        ) : (
          <></>
        )}

        {canAssociationBeDeleted ? (
          <Dropdown.Item
            renderAs="div"
            data-test={`discontinue-association-${user._id}`}
            value={`${user._id}_discontinue`}
          >
            <DeleteAssociationButton
              user={user}
              roleKind={roleKind}
              key={user._id}
              color={'ghost'}
            />
          </Dropdown.Item>
        ) : (
          <></>
        )}
      </Dropdown>
    ),
  }
}

const allColumns: Column<UserListRowData>[] = [
  {
    Header: 'Prénom',
    accessor: 'firstName',
  },
  {
    Header: 'Nom',
    accessor: 'lastName',
  },
  {
    Header: 'Email',
    accessor: 'email',
  },
  {
    Header: 'Matricule',
    accessor: 'internalRef',
  },
  {
    Header: 'Com.',
    accessor: 'internalComment',
  },
  {
    Header: 'Info.',
    accessor: 'internalInformation',
  },
  {
    Header: 'Créé le',
    accessor: 'createdAt',
  },
  {
    Header: 'Rôle',
    accessor: 'organizationRole',
  },
  {
    Header: 'Statut',
    accessor: 'associationStatus',
  },
  {
    Header: 'Statut',
    accessor: 'roleStatus',
  },
  {
    Header: 'Indisponi.',
    accessor: 'availability',
  },
  {
    Header: 'H / Sem.',
    accessor: 'weeklyHoursWorked',
  },
  {
    Header: 'QR',
    accessor: 'qrCode',
  },
  {
    Header: ' ',
    accessor: 'actions',
  },
]
